<script setup lang="ts">
import { ref, computed } from 'vue';
import {
    QForm, useQuasar, type QDateProps, type QInputProps, type QRejectedEntry,
} from 'quasar';
import { AxiosError, isAxiosError } from 'axios';
import type { FaaTestReport } from '@/types/FaaTestReport';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';
import { useSchoolStore } from '@/stores/school';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import { formatDate } from '@/functions/formatDate';
import { isDateValid } from '@/functions/isDateValid';
import type { StandardErrorResponse, ValidationExceptionResponse } from '@/types/Response/StandardErrorResponse';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import DateInput from '@/components/DateInput.vue';

const props = defineProps<{
    student: FlightSchoolCourseStudent;
}>();

/**
 * Todo: change to vue 3.3+ alternative to use more succinct syntax
 * @see: https://vuejs.org/guide/typescript/composition-api.html#typing-component-emits
 */
/* eslint-disable-next-line no-spaced-func, func-call-spacing */
const emit = defineEmits<{
    (event: 'update:reports'): void;
}>();

const $q = useQuasar();
const formRef = ref<QForm|null>(null);
const comment = ref<null|string>(null);
const score = ref<null|string>(null);
const date = ref<null|string>(null);
const uploadFile = ref<null|File>(null);

const acceptedFileTypes = import.meta.env.VITE_ACCEPTED_FILE_EXTENSIONS;
const maxFileSize = import.meta.env.VITE_MAX_FILE_SIZE;
const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth();
const currentDay = new Date().getDate();

const formatMonth = (month: number) => (month < 10 ? `0${month + 1}` : month + 1);

const dateMinYearMonth = `${currentYear - 5}/${formatMonth(currentMonth)}`;
const dateMaxYearMonth = `${currentYear}/${formatMonth(currentMonth)}`;

const dateOptions = (dateString: string) => {
    // don't do string comparison for dates. That's dumb.
    const usableDate = new Date(dateString);
    const minimumDate = new Date(currentYear - 5, currentMonth, currentDay);
    const maximumDate = new Date(currentYear, currentMonth, currentDay);

    return usableDate >= minimumDate && usableDate <= maximumDate;
};

const ruleNotEmpty = (val:string) => ((val && val.length > 0) || 'This field is required');
const ruleOnlyNumbers = (val:string) => (/^[0-9]*$/.test(val) || 'Only numbers are allowed');
const ruleMinScore = (val:number) => (val >= 0 || 'Score must be greater than or equal to 0');
const ruleMaxScore = (val:number) => (val <= 100 || 'Score must be less than or equal to 100');

const ruleValidDate = (val:string) => (!val || isDateValid(val, 'MM/DD/YYYY') || 'Please enter a valid date in the format MM/DD/YYYY');
const ruleDateBeforeToday = (val:string) => ((val && new Date(val) <= new Date()) || !val || 'Date must be today or earlier');
const ruleDateInRange = (val:string) => (!val || dateOptions(val) || 'Date must be within the last 5 years');

const onFileRejected = (rejectedEntries: QRejectedEntry[]) => {
    const rejectedEntry = rejectedEntries[0];
    const { failedPropValidation } = rejectedEntry;

    const fileSizeError = `File must be ${maxFileSize / 1000000}MB or less.`;
    const messages = {
        'max-file-size': fileSizeError,
        'max-total-size': fileSizeError,
        'max-files': 'You can only upload one file at a time. Please try again.',
        accept: 'Invalid file type. Please upload a file with one of the following extensions: .pdf, .jpg, .jpeg, .png, .gif, .heic, .heif',
        filter: 'There was an error with the file you selected. Please try again.',
        duplicate: 'You have already selected this file. Please select a different file.',
    };

    $q.notify({
        color: 'red-5',
        textColor: 'white',
        message: messages[failedPropValidation],
        icon: 'warning',
    });
};

const schoolStore = useSchoolStore();
const submissionLoading = ref<boolean>(false);

const resetForm = () => {
    comment.value = null;
    score.value = null;
    date.value = null;
    uploadFile.value = null;
    formRef.value?.reset();
};

const errorMessages = {
    default: 'There was an error submitting the FAA Test Report. Please try again.',
    rejected: 'There was an error with the file you selected. Please try again or select a different file.',
    mime: 'Invalid file type. Please upload a file with one of the following extensions: .pdf, .jpg, .jpeg, .png, .gif, .heic, .heif',
    large: 'The file you selected is too large. Please select a file that is 5MB or less.',
};

const createFaaTestReport = async (report: FaaTestReport) : Promise<void> => {
    submissionLoading.value = true;

    // This shouldn't ever happen with our route guards, but just in case
    if (!schoolStore.school || !schoolStore.course) {
        return;
    }

    try {
        await FlightSchoolRepository.createFaaTestReport(
            schoolStore.school as FlightSchool,
            schoolStore.course as FlightSchoolCourse,
            props.student,
            report,
        );

        $q.notify({
            color: 'green-4',
            textColor: 'white',
            icon: 'cloud_done',
            message: 'FAA Test Report submitted successfully.',
        });

        emit('update:reports');
        resetForm();
    } catch (error) {
        let errorMessage = errorMessages.default;

        if (isAxiosError(error)) {
            const axiosError = error as AxiosError<StandardErrorResponse|ValidationExceptionResponse>;

            if (axiosError.response?.status === 400) {
                if (
                    'detail' in axiosError.response.data
                    && axiosError.response.data.detail.includes('rejected')
                ) {
                    errorMessage = errorMessages.rejected;
                } else if ('context' in axiosError.response.data) {
                    if (axiosError.response.data.context.violations.some((violation) => violation.message.includes('mime'))) {
                        errorMessage = errorMessages.mime;
                    } else if (axiosError.response.data.context.violations.some((violation) => violation.message.includes('large'))) {
                        errorMessage = errorMessages.large;
                    }
                }
            }
        }

        /**
         * @todo this should be replaced when front end error logging is implemented
         * https://gitlab.teamgleim.com/gleim-it/code-repos/ogs3/fsum-vue-app/-/issues/22
         */
        // eslint-disable-next-line no-console
        console.error(error);

        $q.notify({
            color: 'red-5',
            textColor: 'white',
            message: errorMessage,
            icon: 'warning',
        });
    } finally {
        submissionLoading.value = false;
    }
};

const submit = async () => {
    if (!score.value) {
        $q.notify({
            color: 'red-5',
            textColor: 'white',
            message: 'A score is required.',
            icon: 'warning',
        });

        return;
    }
    if (!date.value) {
        $q.notify({
            color: 'red-5',
            textColor: 'white',
            message: 'A date is required.',
            icon: 'warning',
        });

        return;
    }

    // convert score to a number without leading zeros
    const scoreValue = parseInt(score.value, 10);

    const report: FaaTestReport = {
        score: scoreValue,
        // en-CA is used for ISO 8601 date format
        examDate: formatDate(date.value, 'en-CA'),
        comment: comment.value?.trim() || null,
        file: uploadFile.value,
    };

    await createFaaTestReport(report);
};

const dateInputProps = computed((): Omit<QInputProps, 'modelValue'> => ({
    rules: [ruleNotEmpty, ruleValidDate, ruleDateBeforeToday, ruleDateInRange],
    mask: '##/##/####',
    clearable: true,
    outlined: true,
    square: true,
}));

const datePickerProps = computed((): Omit<QDateProps, 'modelValue'> => ({
    options: (value: string) => dateOptions(value),
    navigationMinYearMonth: dateMinYearMonth,
    navigationMaxYearMonth: dateMaxYearMonth,
}));
</script>
<template>
    <q-form
        id="comment-form"
        ref="formRef"
        class="q-gutter-md bg-white q-pa-md q-mx-auto q-my-auto container comment-form"
        @submit="submit"
    >
        <div class="header text-weight-bold">
            Add New Report
        </div>
        <div class="row text-inputs">
            <div class="col-md-4 col-12 q-pb-sm">
                <div class="input-label text-weight-bold">
                    Score*
                </div>
                <q-input
                    v-model="score"
                    class="score-input q-mt-sm q-mr-md"
                    type="text"
                    inputmode="numeric"
                    pattern="\d*"
                    mask="###"
                    lazy-rules
                    :rules="[ruleNotEmpty, ruleOnlyNumbers, ruleMinScore, ruleMaxScore]"
                    required
                    square
                    outlined
                />
            </div>
        </div>
        <div class="row text-inputs">
            <div class="col-md-shrink col-12 q-pb-sm">
                <div class="input-label text-weight-bold">
                    Test Date*
                </div>
                <DateInput
                    :model-value="date"
                    :input-props="dateInputProps"
                    :date-props="datePickerProps"
                    :is-icon-prepended="false"
                    :class="'date-input q-mt-sm q-mr-md'"
                    :placeholder="'MM/DD/YYYY'"
                    :required="true"
                    @update:model-value="date = $event"
                />
            </div>
        </div>
        <div class="row text-inputs">
            <div class="col-12 comments q-pt-md">
                <div class="input-label text-weight-bold">
                    Comment (optional)
                </div>
                <q-input
                    v-model="comment"
                    class="comment-input q-mt-sm q-mr-md"
                    type="textarea"
                    square
                    outlined
                    no-error-icon
                    rows="10"
                />
            </div>
        </div>
        <div class="row text-inputs">
            <div class="col-md-auto col-12 q-pb-sm">
                <div class="input-label column">
                    <span class="text-weight-bold">Upload image or pdf (optional)</span>
                    <span class="sub-text">File must be 5MB or less</span>
                </div>
                <q-file
                    v-model="uploadFile"
                    :accept="acceptedFileTypes"
                    :max-file-size="maxFileSize"
                    class="q-mt-sm q-mr-md"
                    outlined
                    square
                    clearable
                    @rejected="onFileRejected"
                >
                    <template #prepend>
                        <q-icon name="attach_file" />
                    </template>
                </q-file>
            </div>
        </div>
        <q-btn
            id="submit-btn"
            label="Submit"
            type="submit"
            class="submit-btn fsum-button-dark-blue q-mt-lg q-mb-md"
            no-caps
            :loading="submissionLoading"
        />
    </q-form>
</template>
<style scoped lang="scss">
.header {
    font-size: 32px;
}

.text-inputs {

    .input-label {
        font-size: 16px;
        color: var(--fsum-black);

        .sub-text {
            font-size: 14px;
            color: var(--fsum-med-light-gray-alt);
        }
    }

    label {
        padding-top: 5px;
    }

    .comments {
        :deep(.q-textarea textarea) {
            resize: none;
        }
    }

    :deep(.q-field__bottom--animated) {
        padding-left: 0px;
    }
}
</style>
